import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Edit, X, CheckCircle2, Check, Eye, EyeOff, LockKeyhole, Loader2 } from 'lucide-react';
import { API } from '@/lib/helpers';

const RESULT_OPTIONS = [
  { value: 'apos_votar', label: 'Após votar', icon: Eye },
  { value: 'apos_encerrar', label: 'Após encerrar', icon: LockKeyhole },
  { value: 'nunca', label: 'Nunca mostrar', icon: EyeOff },
];

const emptyForm = {
  pergunta: '', tipo: 'multipla_escolha', descricao: '', mostrar_descricao: true,
  opcoes: [{ texto: '', correta: false }, { texto: '', correta: false }],
  permitir_multiplas: false, marcar_correta: false, anonimo: false, resultado_visibilidade: 'apos_votar',
};

export { emptyForm };

export default function VotacaoFormDialog({ open, onOpenChange, editingId, initialForm, reuniaoId, onSaved }) {
  const [form, setForm] = useState(initialForm || { ...emptyForm });
  const [saving, setSaving] = useState(false);

  // Sync form when dialog opens with new data
  const handleOpenChange = (o) => {
    if (o && initialForm) setForm(initialForm);
    onOpenChange(o);
  };

  const addOpcao = () => setForm({ ...form, opcoes: [...form.opcoes, { texto: '', correta: false }] });
  const removeOpcao = (i) => { if (form.opcoes.length > 2) { const o = [...form.opcoes]; o.splice(i, 1); setForm({ ...form, opcoes: o }); } };
  const updateOpcao = (i, field, value) => { const o = [...form.opcoes]; o[i] = { ...o[i], [field]: value }; setForm({ ...form, opcoes: o }); };
  const setCorreta = (i) => { const o = form.opcoes.map((op, idx) => ({ ...op, correta: idx === i })); setForm({ ...form, opcoes: o }); };

  const handleSave = async () => {
    if (!form.pergunta.trim()) { toast.error('A pergunta é obrigatória'); return; }
    if (form.tipo === 'multipla_escolha') {
      const valid = form.opcoes.filter(o => o.texto.trim());
      if (valid.length < 2) { toast.error('Mínimo 2 opções'); return; }
      if (form.marcar_correta && !form.opcoes.some(o => o.correta)) { toast.error('Selecione pelo menos uma opção correta'); return; }
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.tipo === 'texto') { payload.opcoes = []; payload.permitir_multiplas = false; payload.marcar_correta = false; }
      if (reuniaoId) payload.reuniao_id = reuniaoId;
      const url = editingId ? `${API}/votacoes/${editingId}` : `${API}/votacoes`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(payload) });
      if (!res.ok) {
        const text = await res.text();
        let msg = `Erro ${res.status}`;
        try { msg = JSON.parse(text).detail || msg; } catch {}
        throw new Error(msg);
      }
      // Auto-send when created from a reunião
      if (!editingId && reuniaoId) {
        const data = await res.json();
        if (data.votacao_id) {
          await fetch(`${API}/votacoes/${data.votacao_id}/enviar`, { method: 'POST', credentials: 'include' });
        }
      }
      toast.success(editingId ? 'Votação atualizada' : 'Votação criada');
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingId ? 'Editar Votação' : 'Nova Votação'}</DialogTitle>
        </DialogHeader>

        <div>
          {/* Tipo de resposta */}
          <div>
            <label className="text-sm font-semibold text-slate-800 mb-2 block">Tipo de resposta</label>
            <div className="grid grid-cols-2 gap-3">
              <button type="button" onClick={() => setForm({ ...form, tipo: 'multipla_escolha' })}
                className={`p-3 rounded-lg border-2 text-left transition-all ${form.tipo === 'multipla_escolha' ? 'border-indigo-600 bg-indigo-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                data-testid="tipo-multipla">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className={`w-4 h-4 ${form.tipo === 'multipla_escolha' ? 'text-indigo-600' : 'text-slate-400'}`} />
                  <span className={`text-sm font-semibold ${form.tipo === 'multipla_escolha' ? 'text-indigo-700' : 'text-slate-700'}`}>Múltipla escolha</span>
                </div>
                <p className="text-[11px] text-slate-500">Participantes selecionam opções</p>
              </button>
              <button type="button" onClick={() => setForm({ ...form, tipo: 'texto' })}
                className={`p-3 rounded-lg border-2 text-left transition-all ${form.tipo === 'texto' ? 'border-indigo-600 bg-indigo-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                data-testid="tipo-texto">
                <div className="flex items-center gap-2 mb-1">
                  <Edit className={`w-4 h-4 ${form.tipo === 'texto' ? 'text-indigo-600' : 'text-slate-400'}`} />
                  <span className={`text-sm font-semibold ${form.tipo === 'texto' ? 'text-indigo-700' : 'text-slate-700'}`}>Texto livre</span>
                </div>
                <p className="text-[11px] text-slate-500">Participantes escrevem a resposta</p>
              </button>
            </div>
          </div>

          {/* Visibilidade dos resultados */}
          <div className="pt-3 border-t border-slate-100 mt-3">
            <label className="text-sm font-semibold text-slate-800 mb-2 block">Visibilidade dos resultados</label>
            <div className="grid grid-cols-3 gap-2">
              {RESULT_OPTIONS.map(o => {
                const Icon = o.icon;
                const active = form.resultado_visibilidade === o.value;
                return (
                  <button key={o.value} type="button" onClick={() => setForm({ ...form, resultado_visibilidade: o.value })}
                    className={`py-2 px-2 rounded-lg border-2 transition-all flex flex-col items-center gap-1.5 ${active ? 'border-indigo-600 bg-indigo-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                    data-testid={`resultado-${o.value}`}>
                    <Icon className={`w-4 h-4 ${active ? 'text-indigo-600' : 'text-slate-400'}`} />
                    <span className={`text-[11px] font-semibold leading-tight text-center ${active ? 'text-indigo-700' : 'text-slate-700'}`}>{o.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Configurações */}
          <div className="pt-3 border-t border-slate-100 mt-3">
            <label className="text-sm font-semibold text-slate-800 mb-2 block">Configurações</label>
            <div className="rounded-xl border border-slate-200 overflow-hidden divide-y divide-slate-100">
              {form.tipo === 'multipla_escolha' && (
                <>
                  <div className="flex items-center justify-between p-3 bg-white hover:bg-slate-50 transition-colors">
                    <div>
                      <p className="text-sm font-medium text-slate-800">Múltiplas respostas</p>
                      <p className="text-[11px] text-slate-500">Permitir selecionar mais de uma opção</p>
                    </div>
                    <Switch checked={form.permitir_multiplas} onCheckedChange={v => setForm({ ...form, permitir_multiplas: v })} data-testid="toggle-multiplas" />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white hover:bg-slate-50 transition-colors">
                    <div>
                      <p className="text-sm font-medium text-slate-800">Resposta correta</p>
                      <p className="text-[11px] text-slate-500">Marcar qual opção é a correta</p>
                    </div>
                    <Switch checked={form.marcar_correta} onCheckedChange={v => setForm({ ...form, marcar_correta: v })} data-testid="toggle-correta" />
                  </div>
                </>
              )}
              <div className="flex items-center justify-between p-3 bg-white hover:bg-slate-50 transition-colors">
                <div>
                  <p className="text-sm font-medium text-slate-800">Anônimo</p>
                  <p className="text-[11px] text-slate-500">Quando habilitado, não fica visível quem respondeu</p>
                </div>
                <Switch checked={form.anonimo} onCheckedChange={v => setForm({ ...form, anonimo: v })} data-testid="toggle-anonimo" />
              </div>
            </div>
          </div>

          {/* Pergunta */}
          <div className="pt-3 border-t border-slate-100 mt-3">
            <label className="text-sm font-semibold text-slate-800 mb-2 block">Pergunta / Enunciado <span className="text-red-500">*</span></label>
            <Textarea value={form.pergunta} onChange={e => setForm({ ...form, pergunta: e.target.value })} className="min-h-[70px] bg-white border-slate-200 resize-none" data-testid="input-pergunta" />
          </div>

          {/* Opções (apenas múltipla escolha) */}
          {form.tipo === 'multipla_escolha' && (
            <div className="pt-3 border-t border-slate-100 mt-3">
              <label className="text-sm font-semibold text-slate-800 mb-2 block">Opções <span className="text-slate-400 font-normal">(mín. 2)</span></label>
              <div className="space-y-2">
                {form.opcoes.map((op, i) => (
                  <div key={i} className="flex items-center gap-2 bg-white rounded-lg p-2 border border-slate-200">
                    {form.marcar_correta ? (
                      <button type="button" onClick={() => setCorreta(i)}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${op.correta ? 'border-green-500 bg-green-500 shadow-sm' : 'border-slate-300 hover:border-green-400'}`}
                        data-testid={`correta-${i}`}>
                        {op.correta && <Check className="w-3.5 h-3.5 text-white" />}
                      </button>
                    ) : (
                      <span className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 text-xs font-bold text-indigo-600">{i + 1}</span>
                    )}
                    <Input value={op.texto} onChange={e => updateOpcao(i, 'texto', e.target.value)} className="flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 px-1 h-8" data-testid={`input-opcao-${i}`} />
                    {form.opcoes.length > 2 && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full" onClick={() => removeOpcao(i)}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <Button variant="ghost" size="sm" className="mt-2 w-full text-indigo-600 hover:bg-indigo-50 border border-dashed border-indigo-300 rounded-lg" onClick={addOpcao} data-testid="btn-add-opcao">
                <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar opção
              </Button>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-3 border-t border-slate-100 mt-3">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="px-5">Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 px-6" data-testid="btn-salvar-votacao">
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Aguarde...</> : editingId ? 'Salvar alterações' : 'Criar votação'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
